import { useState } from 'react';
import { supabase } from './supabaseClient';

export default function ResetPasswordPage({ onDone }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async () => {
    if (!password || password.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters.' });
      return;
    }
    if (password !== confirm) {
      setMessage({ type: 'error', text: 'Passwords do not match.' });
      return;
    }
    setLoading(true);
    setMessage(null);
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setMessage({ type: 'error', text: error.message });
    } else {
      setDone(true);
    }
    setLoading(false);
  };

  return (
    <div style={styles.page}>
      <div style={styles.stars} />
      <div style={styles.card}>
        <div style={styles.logoLine}>COLLECTION KEEPER · ACCOUNT RECOVERY</div>
        <h1 style={styles.title}>
          SET NEW <span style={styles.titleAccent}>PASSWORD</span>
        </h1>
        <div style={styles.subtitle}>// SECURE YOUR ACCOUNT //</div>
        <div style={styles.divider} />

        {done ? (
          <>
            <div style={styles.msgSuccess}>
              Password updated successfully. You're now signed in.
            </div>
            <button style={styles.btn} onClick={onDone}>
              GO TO MY COLLECTION
            </button>
          </>
        ) : (
          <>
            <div style={styles.field}>
              <label style={styles.label}>NEW PASSWORD</label>
              <input
                style={styles.input}
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                autoFocus
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>CONFIRM PASSWORD</label>
              <input
                style={styles.input}
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                placeholder="••••••••"
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              />
            </div>

            {message && (
              <div style={message.type === 'error' ? styles.msgError : styles.msgSuccess}>
                {message.text}
              </div>
            )}

            <button style={styles.btn} onClick={handleSubmit} disabled={loading}>
              {loading ? 'UPDATING...' : 'SET NEW PASSWORD'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'var(--bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: "'Rajdhani', sans-serif",
    position: 'relative',
  },
  stars: {
    position: 'fixed', inset: 0, opacity: 'var(--stars-opacity)',
    backgroundImage: `
      radial-gradient(1px 1px at 10% 20%, rgba(255,255,255,0.6) 0%, transparent 100%),
      radial-gradient(1px 1px at 30% 50%, rgba(255,255,255,0.4) 0%, transparent 100%),
      radial-gradient(1.5px 1.5px at 50% 10%, rgba(255,255,255,0.7) 0%, transparent 100%),
      radial-gradient(1px 1px at 70% 70%, rgba(255,255,255,0.5) 0%, transparent 100%),
      radial-gradient(1px 1px at 85% 30%, rgba(255,255,255,0.4) 0%, transparent 100%)
    `,
    pointerEvents: 'none',
  },
  card: {
    position: 'relative',
    width: '100%',
    maxWidth: '440px',
    background: 'var(--panel-deep)',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    padding: '40px',
    boxShadow: '0 0 60px rgba(var(--cyan-rgb),0.06), 0 0 120px rgba(0,0,0,0.8)',
  },
  logoLine: {
    fontFamily: "'Orbitron', monospace",
    fontSize: '9px',
    letterSpacing: '6px',
    color: 'var(--brand-gold)',
    textAlign: 'center',
    marginBottom: '12px',
    opacity: 0.8,
  },
  title: {
    fontFamily: "'Orbitron', monospace",
    fontSize: '28px',
    fontWeight: 900,
    textAlign: 'center',
    color: '#fff',
    lineHeight: 1.1,
    textShadow: '0 0 40px rgba(var(--cyan-rgb),0.3)',
    margin: 0,
  },
  titleAccent: { color: 'var(--gold)' },
  subtitle: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px',
    color: 'var(--muted-2)',
    textAlign: 'center',
    letterSpacing: '3px',
    marginTop: '6px',
  },
  divider: {
    height: '1px',
    background: 'linear-gradient(90deg, transparent, var(--border), var(--brand-gold), var(--border), transparent)',
    margin: '24px 0',
  },
  field: { marginBottom: '16px' },
  label: {
    display: 'block',
    fontFamily: "'Orbitron', monospace",
    fontSize: '9px',
    letterSpacing: '2px',
    color: 'var(--muted-2)',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    background: 'var(--panel)',
    border: '1px solid var(--border)',
    color: 'var(--text)',
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '14px',
    padding: '10px 14px',
    borderRadius: '2px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  btn: {
    width: '100%',
    fontFamily: "'Orbitron', monospace",
    fontSize: '11px',
    letterSpacing: '2px',
    padding: '13px',
    background: 'linear-gradient(135deg, rgba(var(--cyan-rgb),0.15), rgba(var(--brand-gold-rgb),0.1))',
    border: '1px solid var(--cyan)',
    color: 'var(--cyan)',
    cursor: 'pointer',
    borderRadius: '2px',
    marginTop: '8px',
  },
  msgError: {
    background: 'rgba(var(--red-rgb),0.1)',
    border: '1px solid rgba(var(--red-rgb),0.3)',
    color: 'var(--red)',
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px',
    padding: '10px 14px',
    borderRadius: '2px',
    marginBottom: '12px',
    lineHeight: 1.5,
  },
  msgSuccess: {
    background: 'rgba(var(--green-rgb),0.08)',
    border: '1px solid rgba(var(--green-rgb),0.3)',
    color: 'var(--green)',
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px',
    padding: '10px 14px',
    borderRadius: '2px',
    marginBottom: '12px',
    lineHeight: 1.5,
  },
};
